
import React, { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PlusCircle, ShoppingCart, Link2, Tag, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import EcommercePlatformSelector from '@/components/admin/campaigns/ecommerce/EcommercePlatformSelector';
import EcommerceIntegrationSetup from '@/components/admin/campaigns/ecommerce/EcommerceIntegrationSetup';
import EcommerceCampaignsList from '@/components/admin/campaigns/ecommerce/EcommerceCampaignsList';
import EcommerceAnalytics from '@/components/admin/campaigns/ecommerce/EcommerceAnalytics';
import { EcommercePlatform } from '@/components/admin/campaigns/types';

const AdminEcommerceCampaigns = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('platforms');
  const [selectedPlatform, setSelectedPlatform] = useState<EcommercePlatform | null>(null);
  
  const handleLogout = () => {
    navigate('/login');
  };
  
  const handlePlatformSelect = (platform: EcommercePlatform) => {
    setSelectedPlatform(platform);
    setActiveTab('setup');
    
    toast({
      title: "Platform Selected",
      description: `You've selected ${platform} as your e-commerce platform.`,
    });
  };
  
  const handleBackToPlatforms = () => {
    setSelectedPlatform(null);
    setActiveTab('platforms');
  };

  const handleCreateNewCampaign = () => {
    setActiveTab('platforms');
    
    toast({
      title: "Create New E-commerce Campaign",
      description: "Please select an e-commerce platform to get started.",
    });
  };

  // New: handle returning to main campaigns page
  const handleBackToCampaigns = () => {
    navigate('/admin/campaigns');
  };

  return (
    <AdminLayout
      isLoading={false}
      onLogout={handleLogout}
      userRole="admin"
      activeTab="ecommerce-campaigns"
    >
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBackToCampaigns} 
              className="mr-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Campaigns
            </Button>
            <h1 className="text-2xl font-bold">E-commerce Campaign Management</h1>
          </div>
          {!selectedPlatform && (
            <Button onClick={handleCreateNewCampaign}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Create New E-commerce Campaign
            </Button>
          )}
          {selectedPlatform && (
            <Button variant="outline" onClick={handleBackToPlatforms}>
              Back to Platforms
            </Button>
          )}
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="platforms" className="flex items-center">
              <ShoppingCart className="mr-2 h-4 w-4" />
              Platforms
            </TabsTrigger>
            <TabsTrigger value="setup" disabled={!selectedPlatform} className="flex items-center">
              <Link2 className="mr-2 h-4 w-4" />
              Integration Setup
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center">
              <Tag className="mr-2 h-4 w-4" />
              Active Campaigns
            </TabsTrigger>
            <TabsTrigger value="analytics">
              Analytics
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="platforms">
            <EcommercePlatformSelector 
              onSelect={handlePlatformSelect}
            />
          </TabsContent>
          
          <TabsContent value="setup">
            {selectedPlatform && (
              <EcommerceIntegrationSetup
                platform={selectedPlatform}
              />
            )}
          </TabsContent>
          
          <TabsContent value="campaigns">
            <EcommerceCampaignsList />
          </TabsContent>
          
          <TabsContent value="analytics">
            <EcommerceAnalytics />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminEcommerceCampaigns;
